import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Mail, 
  Save, 
  Loader2,
  Eye,
  Code,
  KeyRound,
  UserPlus,
  RefreshCw,
  CheckCircle,
  Edit3,
  XCircle,
  Bell,
  Star
} from "lucide-react";
import { PiktableLoader } from "@/components/PiktableLoader";

interface EmailTemplate {
  id: string;
  template_type: string;
  subject: string;
  body_html: string;
  establishment_id: string | null;
}

interface TemplateConfig {
  type: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  variables: { name: string; description: string }[];
  category: 'system' | 'reservation';
}

const templateConfigs: TemplateConfig[] = [
  // System templates
  {
    type: 'user_verification_code',
    label: 'Code de vérification',
    description: 'Email envoyé lors de l\'inscription avec un code à 6 chiffres',
    icon: <KeyRound className="w-5 h-5" />,
    category: 'system',
    variables: [
      { name: '{{verification_code}}', description: 'Code à 6 chiffres' },
      { name: '{{expiry_minutes}}', description: 'Durée de validité (15 min)' },
      { name: '{{email}}', description: 'Adresse email de l\'utilisateur' },
      { name: '{{current_year}}', description: 'Année en cours' },
    ],
  },
  {
    type: 'user_welcome_invitation',
    label: 'Invitation / Bienvenue',
    description: 'Email envoyé par un admin lors de la création d\'un utilisateur',
    icon: <UserPlus className="w-5 h-5" />,
    category: 'system',
    variables: [
      { name: '{{role_label}}', description: 'Rôle (Administrateur / Staff)' },
      { name: '{{setup_link}}', description: 'Lien de configuration du mot de passe' },
      { name: '{{establishment_name}}', description: 'Nom de l\'établissement' },
      { name: '{{email}}', description: 'Adresse email de l\'utilisateur' },
      { name: '{{current_year}}', description: 'Année en cours' },
    ],
  },
  // Reservation templates (master)
  {
    type: 'reservation_confirmation',
    label: 'Confirmation',
    description: 'Email de confirmation de réservation (template maître pour tous les établissements)',
    icon: <CheckCircle className="w-5 h-5" />,
    category: 'reservation',
    variables: [
      { name: '{{customerName}}', description: 'Nom complet du client' },
      { name: '{{reservationDate}}', description: 'Date de la réservation' },
      { name: '{{reservationTime}}', description: 'Heure de la réservation' },
      { name: '{{numberOfPeople}}', description: 'Nombre de personnes' },
      { name: '{{tableName}}', description: 'Nom de la table (si activé)' },
      { name: '{{establishmentName}}', description: 'Nom de l\'établissement' },
      { name: '{{modifyLink}}', description: 'Lien de modification' },
      { name: '{{cancelLink}}', description: 'Lien d\'annulation' },
    ],
  },
  {
    type: 'reservation_modification',
    label: 'Modification',
    description: 'Email de confirmation de modification (template maître)',
    icon: <Edit3 className="w-5 h-5" />,
    category: 'reservation',
    variables: [
      { name: '{{customerName}}', description: 'Nom complet du client' },
      { name: '{{reservationDate}}', description: 'Date de la réservation' },
      { name: '{{reservationTime}}', description: 'Heure de la réservation' },
      { name: '{{numberOfPeople}}', description: 'Nombre de personnes' },
      { name: '{{tableName}}', description: 'Nom de la table (si activé)' },
      { name: '{{establishmentName}}', description: 'Nom de l\'établissement' },
      { name: '{{modifyLink}}', description: 'Lien de modification' },
      { name: '{{cancelLink}}', description: 'Lien d\'annulation' },
    ],
  },
  {
    type: 'reservation_cancellation',
    label: 'Annulation',
    description: 'Email de confirmation d\'annulation (template maître)',
    icon: <XCircle className="w-5 h-5" />,
    category: 'reservation',
    variables: [
      { name: '{{customerName}}', description: 'Nom complet du client' },
      { name: '{{reservationDate}}', description: 'Date de la réservation' },
      { name: '{{reservationTime}}', description: 'Heure de la réservation' },
      { name: '{{numberOfPeople}}', description: 'Nombre de personnes' },
      { name: '{{establishmentName}}', description: 'Nom de l\'établissement' },
    ],
  },
  {
    type: 'reservation_reminder',
    label: 'Rappel',
    description: 'Email de rappel J-1 (template maître)',
    icon: <Bell className="w-5 h-5" />,
    category: 'reservation',
    variables: [
      { name: '{{customerName}}', description: 'Nom complet du client' },
      { name: '{{reservationDate}}', description: 'Date de la réservation' },
      { name: '{{reservationTime}}', description: 'Heure de la réservation' },
      { name: '{{numberOfPeople}}', description: 'Nombre de personnes' },
      { name: '{{tableName}}', description: 'Nom de la table (si activé)' },
      { name: '{{establishmentName}}', description: 'Nom de l\'établissement' },
      { name: '{{modifyLink}}', description: 'Lien de modification' },
      { name: '{{cancelLink}}', description: 'Lien d\'annulation' },
    ],
  },
  {
    type: 'reservation_followup',
    label: 'Suivi',
    description: 'Email de remerciement post-visite (template maître)',
    icon: <Star className="w-5 h-5" />,
    category: 'reservation',
    variables: [
      { name: '{{customerName}}', description: 'Nom complet du client' },
      { name: '{{establishmentName}}', description: 'Nom de l\'établissement' },
      { name: '{{reviewLink}}', description: 'Lien vers la plateforme d\'avis' },
      { name: '{{reviewPlatformName}}', description: 'Nom de la plateforme (Google, etc.)' },
    ],
  },
];

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editedTemplates, setEditedTemplates] = useState<Record<string, { subject: string; body_html: string }>>({});
  const [previewMode, setPreviewMode] = useState<Record<string, boolean>>({});
  const [activeCategory, setActiveCategory] = useState<'system' | 'reservation'>('system');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const allTypes = templateConfigs.map(c => c.type);
      
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .is('establishment_id', null)
        .in('template_type', allTypes);

      if (error) throw error;

      setTemplates(data || []);
      
      // Initialize edited templates state
      const edited: Record<string, { subject: string; body_html: string }> = {};
      data?.forEach(t => {
        edited[t.template_type] = { subject: t.subject, body_html: t.body_html };
      });
      setEditedTemplates(edited);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Erreur lors du chargement des templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (templateType: string) => {
    const template = templates.find(t => t.template_type === templateType);
    const edited = editedTemplates[templateType];
    
    if (!template || !edited) return;

    setSaving(templateType);
    try {
      const { error } = await supabase
        .from('email_templates')
        .update({
          subject: edited.subject,
          body_html: edited.body_html,
        })
        .eq('id', template.id);

      if (error) throw error;

      toast.success('Template maître sauvegardé');
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(null);
    }
  };

  const getPreviewHtml = (templateType: string) => {
    const edited = editedTemplates[templateType];
    if (!edited) return '';

    let html = edited.body_html;

    // System templates
    if (templateType === 'user_verification_code') {
      html = html
        .replace(/\{\{verification_code\}\}/g, '123456')
        .replace(/\{\{expiry_minutes\}\}/g, '15')
        .replace(/\{\{email\}\}/g, 'utilisateur@exemple.com')
        .replace(/\{\{current_year\}\}/g, String(new Date().getFullYear()));
    } else if (templateType === 'user_welcome_invitation') {
      html = html
        .replace(/\{\{role_label\}\}/g, 'Administrateur')
        .replace(/\{\{setup_link\}\}/g, 'https://exemple.com/set-password#token=xxx')
        .replace(/\{\{establishment_name\}\}/g, 'Mon Restaurant')
        .replace(/\{\{email\}\}/g, 'utilisateur@exemple.com')
        .replace(/\{\{current_year\}\}/g, String(new Date().getFullYear()));
    } 
    // Reservation templates
    else {
      html = html
        .replace(/\{\{customerName\}\}/g, 'Jean Dupont')
        .replace(/\{\{reservationDate\}\}/g, '31/01/2026')
        .replace(/\{\{reservationTime\}\}/g, '19:30')
        .replace(/\{\{numberOfPeople\}\}/g, '4')
        .replace(/\{\{tableName\}\}/g, '<li style="padding: 8px 0;">🪑 <strong>Table :</strong> Table 5</li>')
        .replace(/\{\{establishmentName\}\}/g, 'Restaurant Exemple')
        .replace(/\{\{modifyLink\}\}/g, '#')
        .replace(/\{\{cancelLink\}\}/g, '#')
        .replace(/\{\{reviewLink\}\}/g, 'https://g.page/example')
        .replace(/\{\{reviewPlatformName\}\}/g, 'Google');
    }

    return html;
  };

  const hasChanges = (templateType: string) => {
    const template = templates.find(t => t.template_type === templateType);
    const edited = editedTemplates[templateType];
    if (!template || !edited) return false;
    return template.subject !== edited.subject || template.body_html !== edited.body_html;
  };

  const systemTemplates = templateConfigs.filter(c => c.category === 'system');
  const reservationTemplates = templateConfigs.filter(c => c.category === 'reservation');

  if (loading) {
    return <PiktableLoader message="Chargement des templates..." />;
  }

  const renderTemplateEditor = (config: TemplateConfig) => {
    const edited = editedTemplates[config.type] || { subject: '', body_html: '' };
    const isPreview = previewMode[config.type];
    const templateExists = templates.some(t => t.template_type === config.type);

    if (!templateExists) {
      return (
        <TabsContent key={config.type} value={config.type} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {config.icon}
                {config.label}
              </CardTitle>
              <CardDescription>{config.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <p>Template maître non trouvé en base de données.</p>
                <p className="text-sm mt-2">Ce template sera créé automatiquement lors de la prochaine migration.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      );
    }

    return (
      <TabsContent key={config.type} value={config.type} className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {config.icon}
                  {config.label}
                  <Badge variant="secondary" className="ml-2">Maître</Badge>
                </CardTitle>
                <CardDescription>{config.description}</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {hasChanges(config.type) && (
                  <Badge variant="secondary">Non sauvegardé</Badge>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPreviewMode(prev => ({ ...prev, [config.type]: !prev[config.type] }))}
                >
                  {isPreview ? <Code className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                  {isPreview ? 'Code' : 'Aperçu'}
                </Button>
                <Button
                  onClick={() => handleSave(config.type)}
                  disabled={saving === config.type || !hasChanges(config.type)}
                >
                  {saving === config.type ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Sauvegarder
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor={`subject-${config.type}`}>Sujet de l'email</Label>
              <Input
                id={`subject-${config.type}`}
                value={edited.subject}
                onChange={(e) => setEditedTemplates(prev => ({
                  ...prev,
                  [config.type]: { ...prev[config.type], subject: e.target.value }
                }))}
                placeholder="Sujet de l'email..."
              />
            </div>

            {/* Body */}
            <div className="space-y-2">
              <Label>Corps de l'email (HTML)</Label>
              {isPreview ? (
                <div 
                  className="border rounded-lg p-4 bg-white min-h-[400px] overflow-auto"
                  dangerouslySetInnerHTML={{ __html: getPreviewHtml(config.type) }}
                />
              ) : (
                <Textarea
                  value={edited.body_html}
                  onChange={(e) => setEditedTemplates(prev => ({
                    ...prev,
                    [config.type]: { ...prev[config.type], body_html: e.target.value }
                  }))}
                  className="font-mono text-sm min-h-[400px]"
                  placeholder="Code HTML de l'email..."
                />
              )}
            </div>

            {/* Variables */}
            <div className="space-y-2">
              <Label>Variables disponibles</Label>
              <div className="flex flex-wrap gap-2">
                {config.variables.map(v => (
                  <Badge
                    key={v.name}
                    variant="outline"
                    className="cursor-pointer hover:bg-muted"
                    onClick={() => {
                      navigator.clipboard.writeText(v.name);
                      toast.success(`${v.name} copié`);
                    }}
                    title={v.description}
                  >
                    {v.name}
                  </Badge>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                Cliquez sur une variable pour la copier
              </p>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container mx-auto p-6 max-w-5xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-serif font-bold flex items-center gap-3">
                <Mail className="w-8 h-8 text-primary" />
                Templates email (maîtres)
              </h1>
              <p className="text-muted-foreground mt-1">
                Templates globaux utilisés par défaut par tous les établissements
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchTemplates}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Category Tabs */}
        <Tabs value={activeCategory} onValueChange={(v) => setActiveCategory(v as 'system' | 'reservation')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="system" className="flex items-center gap-2">
              <KeyRound className="w-4 h-4" />
              Système ({systemTemplates.length})
            </TabsTrigger>
            <TabsTrigger value="reservation" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Réservations ({reservationTemplates.length})
            </TabsTrigger>
          </TabsList>

          {/* System Templates */}
          <TabsContent value="system" className="mt-6">
            <Tabs defaultValue="user_verification_code" className="space-y-6">
              <TabsList className="grid w-full grid-cols-2">
                {systemTemplates.map(config => (
                  <TabsTrigger key={config.type} value={config.type} className="flex items-center gap-2">
                    {config.icon}
                    {config.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {systemTemplates.map(config => renderTemplateEditor(config))}
            </Tabs>
          </TabsContent>

          {/* Reservation Templates */}
          <TabsContent value="reservation" className="mt-6">
            <div className="mb-4 p-4 bg-muted/50 rounded-lg border">
              <p className="text-sm text-muted-foreground">
                <strong>Templates maîtres :</strong> Ces templates servent de base pour tous les établissements. 
                Quand un manager clique sur "Réinitialiser", son template est remplacé par le contenu de ces templates maîtres.
              </p>
            </div>
            <Tabs defaultValue="reservation_confirmation" className="space-y-6">
              <TabsList className="grid w-full grid-cols-5">
                {reservationTemplates.map(config => (
                  <TabsTrigger key={config.type} value={config.type} className="flex items-center gap-1 text-xs">
                    {config.icon}
                    <span className="hidden sm:inline">{config.label}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
              {reservationTemplates.map(config => renderTemplateEditor(config))}
            </Tabs>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
